// Operator-driven Plaid Hosted Link. Mints a single-use invitation, opens
// Plaid's hosted_link_url, and waits for the SESSION_FINISHED webhook to
// create the Item. No cdn.plaid.com, no /link/exchange from the console.
// Invitation remains the session identity.

import type { AdminClient } from "../api.js";
import { settleReads } from "../read-cache.js";
import { outcomeSentence, AuthoredRefusal } from "../ui/failure.js";
import { publishHealth } from "../ui/nav-bus.js";
import { navLabel } from "../ui/sentences.js";
import { forgetPossiblySent, runWrite, type WriteOutcome } from "../ui/write.js";
import type { CompanyHealth, CompanySlug, LinkExchangeResponse } from "../types.js";

const INVITATION_TTL_MS = 60 * 60 * 1000;

/** Match Worker EXCHANGE_WINDOW_MS / Hosted Link URL lifetime. */
export const HOSTED_LINK_WAIT_MS = 30 * 60 * 1000;

/**
 * The wait never promises success: a person who closed the bank window has to
 * start the link again, and only a fresh health reading says whether it
 * finished (rd R11 R6-15).
 */
export const BANK_WAIT_COPY =
  "Waiting for the bank to finish… If you closed the bank window, start the link again.";
export const BANK_WAIT_TIMEOUT_COPY =
  `Timed out waiting for the bank connection. If you closed the bank window, start the link again. If you finished, refresh ${navLabel("balances")}.`;
export const BANK_WAIT_STOPPED_COPY = "Stopped waiting for the bank connection.";

/**
 * The wait ran out (B2b): a TYPE, not a message match, so a caller never has to
 * read `error.message` to tell our own sentence from a server failure.
 */
export class BankWaitTimeout extends Error {
  constructor() {
    super(BANK_WAIT_TIMEOUT_COPY);
    this.name = "BankWaitTimeout";
  }
}
/**
 * One confirmation for the one flow (B2b: this literal was typed five times,
 * on Bank accounts and Connection status). The connect flow owns the words, so
 * every surface that reports its success reads them from here.
 */
export const ACCOUNT_CONNECTED = "Account connected.";

/**
 * The connected items of one company's health reading.
 *
 * B2b-1: "is a bank connected" everywhere is `bankConnection(health)`. This
 * function is the ONE place the page reads the item list, because the wait must
 * know item IDENTITY (which item was there before, which one was broken) — a
 * different question from "is there a bank". Keeping the read in one function
 * is what the facts guard can see: one recorded "correct use" entry, not four.
 */
function plaidItemsOf(
  health: CompanyHealth | null | undefined,
): readonly CompanyHealth["plaidItems"][number][] {
  return health?.plaidItems ?? [];
}

/**
 * The write fingerprint for one step of the link flow (B2b: a slug inside a
 * template is what the brand guard cannot tell from a leak). It is an internal
 * key, never words: `runWrite` holds one flight per company and step, so a
 * second press cannot mint a second invitation (rc-2 sister 7).
 */
function flowKey(step: "invite" | "session" | "revoke", company: CompanySlug): string {
  return `link-${step}:${company}`;
}

/**
 * A second press after a lost invite response (round 2 review, M3). The first
 * mint may have created an invitation, so `runWrite` refuses the retry; this
 * error carries the decided blocked sentence and tells the page to offer the
 * way out. It extends `AuthoredRefusal`, the only error whose own message
 * becomes a sentence, so `failureCopy` shows these words rather than the
 * generic "could not confirm it saved".
 */
export class BankInvitePossiblySent extends AuthoredRefusal {
  /** The decided blocked sentence, exposed for pages the error-text guard reads. */
  readonly sentence: string;

  constructor() {
    const words = outcomeSentence({ kind: "unknown", blocked: true }, "owner");
    super(words);
    this.name = "BankInvitePossiblySent";
    this.sentence = words;
  }
}

/**
 * The way out of a blocked invitation: forget the possibly-sent key so the
 * next press mints a fresh invitation. An invitation is the server's, not a
 * row the page can read back, so "Start again" is the honest control.
 */
export function startLinkAgain(company: CompanySlug): void {
  forgetPossiblySent(flowKey("invite", company));
}

/**
 * A "Start again" control for a page's failure face. It clears the blocked
 * invite and removes itself; the Connect or Reconnect button is already
 * re-enabled, so the next press is an ordinary first attempt.
 */
export function startAgainControl(
  company: CompanySlug,
  onCleared?: () => void,
): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "btn btn-secondary";
  button.setAttribute("data-action", "start-bank-link-again");
  button.textContent = "Start again";
  button.addEventListener("click", () => {
    startLinkAgain(company);
    onCleared?.();
    button.remove();
  });
  return button;
}

/**
 * The scope `runWrite` disables while a step runs. The page passes the control
 * the person pressed; a caller with no page (a flow test) gets a detached
 * element, so nothing on a live page is disabled by accident.
 */
function scopeOf(scope: HTMLElement | undefined): HTMLElement {
  return scope ?? document.createElement("div");
}

/**
 * The error the flow throws for a write step that did not land. The ORIGINAL
 * error is rethrown when there is one, so the page's `failureCopy` still reads
 * decision 1's 403 sentence, "already answered", or a lost response correctly.
 */
function flowError(outcome: WriteOutcome): Error {
  const error = "error" in outcome ? outcome.error : undefined;
  if (error instanceof Error) return error;
  return new Error(outcomeSentence(outcome, "owner"));
}

const DEFAULT_POLL_INTERVAL_MS = 2000;

export type OpenHostedLinkUrl = (url: string) => Window | null;

function defaultOpenUrl(url: string): Window | null {
  return window.open(url, "_blank", "noopener,noreferrer");
}

function defaultSleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** The company's connections as the health read described them at the start. */
interface ConnectionSnapshot {
  itemIds: Set<string>;
  /**
   * Connections that were NOT healthy. The Worker repairs a company's single
   * broken Item IN PLACE — same Plaid item id, status back to `healthy` — when
   * update mode is used, so a new id never appears for that repair.
   */
  brokenIds: Set<string>;
  /**
   * The item a Reconnect was pressed for, when the person picked one. Success
   * is THAT item turning healthy, never another bank healing at the same time
   * (rd R11 R6-14).
   */
  expectedItemId: string | null;
  /** The institution of that item, used only when the id came back changed. */
  expectedInstitution: string | null;
}

async function snapshotConnections(
  client: AdminClient,
  company: CompanySlug,
  expectedItemId: string | null,
  expectedInstitution: string | null,
): Promise<ConnectionSnapshot> {
  const { companies } = await client.getFleetHealth();
  const row = companies.find((entry) => entry.company === company);
  const items = plaidItemsOf(row);
  return {
    itemIds: new Set(items.map((item) => item.plaidItemId)),
    brokenIds: new Set(
      items
        .filter((item) => item.status !== "healthy")
        .map((item) => item.plaidItemId),
    ),
    expectedItemId,
    expectedInstitution,
  };
}

interface WaitOptions {
  client: AdminClient;
  company: CompanySlug;
  snapshot: ConnectionSnapshot;
  onStatus: (message: string) => void;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  pollIntervalMs: number;
  waitMs: number;
  /** False once another page owns the screen; the poll must stop. */
  isCurrent?: () => boolean;
}

async function waitForNewPlaidItem(opts: WaitOptions): Promise<string> {
  try {
    return await pollForConnectedItem(opts);
  } finally {
    // Found, or gave up: the new connection must not hide behind the read
    // cache (rd-x2).
    settleReads(opts.client);
  }
}

/**
 * One connection is "done" when either:
 *  - a Plaid item id appears that was not there when the flow started (a new
 *    connection), or
 *  - an Item that was NOT healthy at the start is healthy now (the Worker
 *    repaired it in place, same id — `finalizeLinkedItem` in
 *    ingest-worker/src/plaid.ts). Without the second case a company with one
 *    broken bank waits out the whole window and then reports a false failure
 *    after a real repair.
 *
 * A Reconnect named its item: only that item counts, or (when the repair came
 * back under a new id) an item at the same institution that was new or broken
 * at the start — never one that was already healthy (R6-14, round 1 review 2).
 * A generic Connect counts the in-place repair only when there was exactly one
 * broken item at the start: that item is the only possible repair, even in a
 * company with several banks (round 1 review 1).
 */
function connectedItem(
  items: readonly CompanyHealth["plaidItems"][number][],
  snapshot: ConnectionSnapshot,
): string | null {
  if (snapshot.expectedItemId !== null) {
    const target = items.find((item) => item.plaidItemId === snapshot.expectedItemId);
    if (target !== undefined && target.status === "healthy") {
      return target.plaidItemId;
    }
    // Most repairs keep the item id. When the bank window reported the bank
    // as a fresh connection, the institution is the only thing that ties it to
    // the Reconnect the person pressed; claim it only when exactly one healthy
    // item carries that institution AND that item was not already healthy when
    // the flow started. A healthy item at the same institution is a different
    // bank, not this flow's repair (round 1 review, HIGH 2).
    //
    // Known limit, and why the guards above are the most this can do: two banks
    // at one institution are indistinguishable by name, so if the other tab's
    // flow heals the item this snapshot recorded as broken, this flow reports
    // it as its own. The console sees no item-level owner to check against —
    // the invitations are not item-scoped (R6-14, deferred on the Worker side).
    // Keep the "was not healthy at the start" filter: it is what stops an
    // already-healthy sibling from being claimed.
    const institution = snapshot.expectedInstitution?.trim() ?? "";
    if (institution !== "") {
      const sameBank = items.filter(
        (item) =>
          item.status === "healthy" &&
          (item.institution ?? "").trim() === institution &&
          (!snapshot.itemIds.has(item.plaidItemId) ||
            snapshot.brokenIds.has(item.plaidItemId)),
      );
      if (sameBank.length === 1) {
        return sameBank[0]?.plaidItemId ?? null;
      }
    }
    return null;
  }
  const added = items.find((item) => !snapshot.itemIds.has(item.plaidItemId));
  if (added !== undefined) return added.plaidItemId;
  // With one broken item at the start, only that item can be the repair — no
  // matter how many other banks the company has (round 1 review, HIGH 1). The
  // earlier shape also demanded a single item, so a two-bank company with one
  // broken bank timed out after a real in-place repair.
  if (snapshot.brokenIds.size === 1) {
    const onlyId = [...snapshot.brokenIds][0] ?? "";
    const repaired = items.find(
      (item) => item.plaidItemId === onlyId && item.status === "healthy",
    );
    return repaired === undefined ? null : repaired.plaidItemId;
  }
  return null;
}

async function pollForConnectedItem(opts: WaitOptions): Promise<string> {
  const deadline = opts.now() + opts.waitMs;
  // The plain progress line is kept first because other surfaces (and their
  // tests) already read it; the sentence the person keeps is the one below,
  // which says what to do when the bank window was closed (R6-15).
  opts.onStatus("Waiting for the bank to finish…");
  opts.onStatus(BANK_WAIT_COPY);
  while (opts.now() < deadline) {
    if (opts.isCurrent !== undefined && !opts.isCurrent()) {
      throw new Error(BANK_WAIT_STOPPED_COPY);
    }
    const { companies } = await opts.client.getFleetHealth();
    const row = companies.find((entry) => entry.company === opts.company);
    const found = connectedItem(plaidItemsOf(row), opts.snapshot);
    if (found !== null) {
      // Publish the reading this flow just saw, so the banner, Home and Bank
      // accounts agree that the bank is connected again: they all speak for
      // the shell's last reading, and this one is newer than that.
      publishHealth({ companies });
      return found;
    }
    const remaining = deadline - opts.now();
    if (remaining <= 0) {
      break;
    }
    await opts.sleep(Math.min(opts.pollIntervalMs, remaining));
  }
  throw new BankWaitTimeout();
}

/**
 * Mint an invitation, open Hosted Link, wait for the Worker to store the
 * Item. Revoke only if session mint fails before Plaid is opened — an async
 * flow has no `finally` once the user is on Plaid's page.
 */
export async function connectBankAccount(opts: {
  client: AdminClient;
  company: CompanySlug;
  onStatus?: (message: string) => void;
  openUrl?: OpenHostedLinkUrl;
  sleep?: (ms: number) => Promise<void>;
  now?: () => number;
  pollIntervalMs?: number;
  waitMs?: number;
  /** The broken item a Reconnect was pressed for; null for a new bank. */
  expectedItemId?: string;
  /** That item's institution, for a repair that returns a new item id. */
  expectedInstitution?: string | null;
  /** False once the page is gone: stop polling instead of waiting it out. */
  isCurrent?: () => boolean;
  /** The control the person pressed: the flow disables it while a step runs. */
  scope?: HTMLElement;
}): Promise<LinkExchangeResponse> {
  const { client, company } = opts;
  const onStatus = opts.onStatus ?? ((): void => undefined);
  const openUrl = opts.openUrl ?? defaultOpenUrl;
  const sleep = opts.sleep ?? defaultSleep;
  const now = opts.now ?? Date.now;
  const snapshot = await snapshotConnections(
    client,
    company,
    opts.expectedItemId ?? null,
    opts.expectedInstitution ?? null,
  );

  /**
   * The page's `isCurrent` is consumed ONCE per read of this function's own
   * steps, never by the write primitive: `runWrite` asks `stillCurrent` before
   * `afterLanded`, and a page predicate that counts its calls (the "did the
   * person leave?" test) must not be spent on a bookkeeping question. Each step
   * below is one request, so it is always allowed to finish and report.
   */
  const alwaysCurrent = (): boolean => true;

  onStatus("Preparing connection…");
  // rc-2 sister 7: the mint is a write through the one primitive, so a second
  // press cannot mint a second invitation.
  let issued: Awaited<ReturnType<AdminClient["issueLinkInvitation"]>> | undefined;
  const mint = await runWrite({
    key: flowKey("invite", company),
    scope: scopeOf(opts.scope),
    gate: () => null,
    send: () =>
      client.issueLinkInvitation(company, {
        expiresAt: new Date(Date.now() + INVITATION_TTL_MS).toISOString(),
        label: "console-connect",
      }),
    afterLanded: (value) => {
      issued = value;
    },
    // Round 2 review, M3 (rc-2 sister 7): a lost invite response may already
    // have created an invitation, so a second press must not mint another.
    blockWhenPossiblySent: true,
    stillCurrent: alwaysCurrent,
  });
  if (issued === undefined) {
    if (mint.kind === "unknown" && mint.blocked === true) {
      throw new BankInvitePossiblySent();
    }
    throw flowError(mint);
  }
  const invitation = issued;

  onStatus("Opening bank connection…");
  let session: Awaited<ReturnType<AdminClient["openLinkSession"]>> | undefined;
  let openedSession: WriteOutcome;
  try {
    // The session write is the only write in this block. Its failure is the one
    // case that must drop the invitation we just minted (nothing was shown to
    // the person yet), so the cleanup sits in its own catch — a re-read after a
    // write inside one try is what `reload-after-write` forbids.
    openedSession = await runWrite({
      key: flowKey("session", company),
      scope: scopeOf(opts.scope),
      gate: () => null,
      send: () => client.openLinkSession(invitation.token),
      afterLanded: (value) => {
        session = value;
      },
      stillCurrent: alwaysCurrent,
    });
  } catch (error) {
    await revokeInvitation();
    throw error;
  }
  if (session === undefined) {
    await revokeInvitation();
    throw flowError(openedSession);
  }
  const linked = session;

  // From here the Hosted Link URL has been minted for this invitation: it is
  // either opened or shown to the person, so nothing revokes it. A wait that
  // timed out is said in its own words by the page, never as a failed save.
  let opened: Window | null;
  try {
    opened = openUrl(linked.hostedLinkUrl);
  } catch {
    opened = null;
  }
  if (opened === null) {
    onStatus(
      `Popup blocked. Open this URL to continue: ${linked.hostedLinkUrl}`,
    );
  }
  const plaidItemId = await waitForNewPlaidItem({
    client,
    company,
    snapshot,
    onStatus,
    sleep,
    now,
    pollIntervalMs: opts.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS,
    waitMs: opts.waitMs ?? HOSTED_LINK_WAIT_MS,
    ...(opts.isCurrent === undefined ? {} : { isCurrent: opts.isCurrent }),
  });
  return { company, plaidItemId };

  /**
   * Drop an invitation the person never saw, and hand its outcome back so the
   * caller decides what to say. Its failure needs no words: an unused
   * invitation expires with its own window, and the person already sees the
   * failure that led here.
   */
  function revokeInvitation(): Promise<WriteOutcome> {
    return runWrite({
      key: flowKey("revoke", company),
      scope: scopeOf(opts.scope),
      gate: () => null,
      send: () => client.revokeLinkInvitation(invitation.id),
      stillCurrent: alwaysCurrent,
    });
  }
}
